import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import UserDashboardPage from "@/pages/UserDashboardPage";
import MyWalletPage from "@/pages/MyWalletPage";
import TicketSuccessPage from "@/pages/TicketSuccessPage";
import TicketRetrievePage from "@/pages/TicketRetrievePage";
import DMPage from "@/pages/DMPage";
import DonatePage from "@/pages/DonatePage";
import DonationThankYou from "@/pages/DonationThankYou";
import BlogPage from "@/pages/BlogPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import GetInvolvedPage from "@/pages/GetInvolvedPage";
import ContactPage from "@/pages/ContactPage";
import TicketVerifyPage from "@/pages/TicketVerifyPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminDonations from "@/pages/admin/AdminDonations";
import AdminSupporters from "@/pages/admin/AdminSupporters";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminNewsletters from "@/pages/admin/AdminNewsletters";
import AdminTicketSales from "@/pages/admin/AdminTicketSales";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSocialMedia from "@/pages/admin/AdminSocialMedia";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminDigest from "@/pages/admin/AdminDigest";
import AdminStreams from "@/pages/admin/AdminStreams";
import AdminSiteChanges from "@/pages/admin/AdminSiteChanges";
import AdminTraining from "@/pages/admin/AdminTraining";
import PodcastPage from "@/pages/PodcastPage";
import InboxPage from "@/pages/InboxPage";
import GalleryPage from "@/pages/GalleryPage";
import CampaignsPage from "@/pages/CampaignsPage";
import CommunityPage from "@/pages/CommunityPage";
import UserProfilePage from "@/pages/UserProfilePage";
import LiveStreamPage from "@/pages/LiveStreamPage";
import ConferenceRoomPage from "@/pages/ConferenceRoomPage";

// Always use the current domain for API calls — works on any deployed domain
const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      // Fallback: try localStorage token for mobile browsers
      const token = localStorage.getItem('bvb_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true 
          });
          setUser(response.data);
        } catch {
          localStorage.removeItem('bvb_token');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Axios interceptor: always send token from localStorage if available
    const interceptor = axios.interceptors.request.use((config) => {
      // Don't add token to login/register requests
      if (config.url?.includes('/auth/login') || config.url?.includes('/auth/register')) return config;
      const token = localStorage.getItem('bvb_token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    checkAuth();
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Store token in localStorage as fallback for mobile browsers that block cookies
    if (response.data.access_token) {
      localStorage.setItem('bvb_token', response.data.access_token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    if (response.data.access_token) {
      localStorage.setItem('bvb_token', response.data.access_token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    localStorage.removeItem('bvb_token');
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setUser(response.data.user);
          navigate('/', { replace: true, state: { user: response.data.user } });
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-blue mx-auto"></div>
        <p className="mt-4 text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location } });
    }
    if (!loading && adminOnly && user) {
      const adminRoles = ['master_admin', 'super_admin', 'developer', 'admin', 'event_manager', 'blog_editor', 'donation_manager', 'ticket_scanner'];
      if (!adminRoles.includes(user.role)) {
        navigate('/');
      }
    }
  }, [user, loading, adminOnly, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-blue"></div>
      </div>
    );
  }

  if (!user) return null;
  if (adminOnly) {
    const adminRoles = ['master_admin', 'super_admin', 'developer', 'admin', 'event_manager', 'blog_editor', 'donation_manager', 'ticket_scanner'];
    if (!adminRoles.includes(user.role)) return null;
  }

  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/events/:eventId" element={<EventDetailPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><UserDashboardPage /></ProtectedRoute>} />
      <Route path="/my-wallet" element={<ProtectedRoute><MyWalletPage /></ProtectedRoute>} />
      <Route path="/payment-success" element={<TicketSuccessPage />} />
      <Route path="/payment-cancel" element={<TicketSuccessPage />} />
      <Route path="/tickets/success" element={<TicketSuccessPage />} />
      <Route path="/tickets/retrieve" element={<TicketRetrievePage />} />
      <Route path="/ticket-success" element={<TicketSuccessPage />} />
      <Route path="/success" element={<TicketSuccessPage />} />
      <Route path="/dm" element={<ProtectedRoute><DMPage /></ProtectedRoute>} />
      <Route path="/dm/:userId" element={<ProtectedRoute><DMPage /></ProtectedRoute>} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/donations/thank-you" element={<DonationThankYou />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:blogId" element={<BlogDetailPage />} />
      <Route path="/get-involved" element={<GetInvolvedPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/campaigns" element={<CampaignsPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/podcast" element={<PodcastPage />} />
      <Route path="/podcast/:podcastId" element={<PodcastPage />} />
      <Route path="/podcast/:podcastId/director" element={<ProtectedRoute adminOnly><PodcastPage /></ProtectedRoute>} />
      <Route path="/podcast/:podcastId/guest" element={<ProtectedRoute><PodcastPage /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<UserProfilePage />} />
      <Route path="/live" element={<LiveStreamPage />} />
      <Route path="/live/:streamId" element={<LiveStreamPage />} />
      <Route path="/conference" element={
        <ProtectedRoute>
          <ConferenceRoomPage />
        </ProtectedRoute>
      } />
      <Route path="/conference/:meetingId" element={
        <ProtectedRoute>
          <ConferenceRoomPage />
        </ProtectedRoute>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/ticket-verify" element={
        <ProtectedRoute>
          <TicketVerifyPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/events" element={
        <ProtectedRoute adminOnly>
          <AdminEvents />
        </ProtectedRoute>
      } />
      <Route path="/admin/blog" element={
        <ProtectedRoute adminOnly>
          <AdminBlog />
        </ProtectedRoute>
      } />
      <Route path="/admin/donations" element={
        <ProtectedRoute adminOnly>
          <AdminDonations />
        </ProtectedRoute>
      } />
      <Route path="/admin/supporters" element={
        <ProtectedRoute adminOnly>
          <AdminSupporters />
        </ProtectedRoute>
      } />
      <Route path="/admin/tickets" element={
        <ProtectedRoute adminOnly>
          <AdminTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute adminOnly>
          <AdminMessages />
        </ProtectedRoute>
      } />
      <Route path="/admin/gallery" element={
        <ProtectedRoute adminOnly>
          <AdminGallery />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute adminOnly>
          <AdminAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/admin/campaigns" element={
        <ProtectedRoute adminOnly>
          <AdminCampaigns />
        </ProtectedRoute>
      } />
      <Route path="/admin/newsletters" element={
        <ProtectedRoute adminOnly>
          <AdminNewsletters />
        </ProtectedRoute>
      } />
      <Route path="/admin/ticket-sales" element={
        <ProtectedRoute adminOnly>
          <AdminTicketSales />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute adminOnly>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/social-media" element={
        <ProtectedRoute adminOnly>
          <AdminSocialMedia />
        </ProtectedRoute>
      } />
      <Route path="/admin/partners" element={
        <ProtectedRoute adminOnly>
          <AdminPartners />
        </ProtectedRoute>
      } />
      <Route path="/admin/digest" element={
        <ProtectedRoute adminOnly>
          <AdminDigest />
        </ProtectedRoute>
      } />
      <Route path="/admin/streams" element={
        <ProtectedRoute adminOnly>
          <AdminStreams />
        </ProtectedRoute>
      } />
      <Route path="/admin/site-changes" element={
        <ProtectedRoute adminOnly>
          <AdminSiteChanges />
        </ProtectedRoute>
      } />
      <Route path="/admin/training" element={
        <ProtectedRoute adminOnly>
          <AdminTraining />
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute>
          <InboxPage />
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
